import { useCallback, useState, useRef, useEffect } from 'react';

/**
 * Custom hook for chart drag functionality
 * Provides consistent dragging behavior across all financial charts
 */
export const useChartDrag = <T extends { month: string }>(
  data: T[],
  monthsToShow: number,
  onDataChange: (newData: T[]) => void
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [viewWindow, setViewWindow] = useState({
    start: Math.max(0, data.length - monthsToShow),
    end: data.length,
  });

  // View window represents which portion of the data is visible
  useEffect(() => {
    if (data.length > 0) {
      const newStart = Math.max(0, data.length - monthsToShow);
      const newEnd = data.length;
      setViewWindow({ start: newStart, end: newEnd });
    }
  }, [data.length, monthsToShow]);

  // Handle touch start / mouse down
  const handleDragStart = useCallback((clientX: number) => {
    setTouchStartX(clientX);
    setIsDragging(true);
  }, []);

  // Handle touch move / mouse move
  const handleDragMove = useCallback(
    (clientX: number) => {
      if (!isDragging || touchStartX === null) return;

      const deltaX = touchStartX - clientX;
      if (Math.abs(deltaX) < 5) return; // Ignore tiny movements

      const containerWidth = containerRef.current?.clientWidth || 300;
      const moveRatio = deltaX / containerWidth;
      const dataRangeSize = data.length;
      const viewWindowSize = viewWindow.end - viewWindow.start;

      // Calculate how much to shift the view window (0.5 = 50% of visible range)
      const shiftAmount = Math.ceil(moveRatio * viewWindowSize * 0.5);

      if (shiftAmount !== 0) {
        // Calculate new window bounds
        let newStart = viewWindow.start + shiftAmount;
        let newEnd = viewWindow.end + shiftAmount;

        // Ensure window stays within data bounds
        if (newStart < 0) {
          newStart = 0;
          newEnd = viewWindowSize;
        }

        // Update view window and visible data
        if (newEnd > dataRangeSize) {
          newEnd = dataRangeSize;
          newStart = Math.max(0, dataRangeSize - viewWindowSize);
        }

        setViewWindow({ start: newStart, end: newEnd });
        onDataChange(data.slice(newStart, newEnd));

        // Reset touch tracking after making the change
        setTouchStartX(clientX);
      }
    },
    [data, isDragging, onDataChange, touchStartX, viewWindow]
  );

  // Handle drag end (mouse up or touch end)
  const handleDragEnd = useCallback(() => {
    setTouchStartX(null);
    setIsDragging(false);
  }, []);

  // Navigation helpers
  const goEarlier = useCallback(() => {
    const newStart = Math.max(0, viewWindow.start - Math.floor(monthsToShow / 2));
    const newEnd = Math.min(data.length, newStart + monthsToShow);
    
    setViewWindow({ start: newStart, end: newEnd });
    onDataChange(data.slice(newStart, newEnd));
  }, [data, monthsToShow, viewWindow.start, onDataChange]);

  const goLater = useCallback(() => {
    const newStart = Math.min(data.length - monthsToShow, viewWindow.start + Math.floor(monthsToShow / 2));
    const newEnd = Math.min(data.length, newStart + monthsToShow);
    
    setViewWindow({ start: newStart, end: newEnd });
    onDataChange(data.slice(newStart, newEnd));
  }, [data, monthsToShow, viewWindow.start, onDataChange]);

  return {
    containerRef,
    isDragging,
    viewWindow,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    goEarlier,
    goLater
  };
};

/**
 * Adds drag event handlers to a JSX element
 * @param handlers - The drag handlers from useChartDrag
 */
export const withDragHandlers = (
  handlers: {
    handleDragStart: (clientX: number) => void;
    handleDragMove: (clientX: number) => void;
    handleDragEnd: () => void;
    isDragging: boolean;
  }
) => {
  const { handleDragStart, handleDragMove, handleDragEnd, isDragging } = handlers;
  
  return {
    onTouchStart: (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        handleDragStart(touch.clientX);
      }
    },
    onTouchMove: (e: React.TouchEvent) => {
      if (e.touches.length === 1 && isDragging) {
        const touch = e.touches[0];
        handleDragMove(touch.clientX);
      }
    },
    onTouchEnd: () => {
      handleDragEnd();
    },
    onMouseDown: (e: React.MouseEvent) => {
      handleDragStart(e.clientX);
    },
    onMouseMove: (e: React.MouseEvent) => {
      if (isDragging) {
        handleDragMove(e.clientX);
      }
    },
    onMouseUp: () => {
      handleDragEnd();
    },
    onMouseLeave: () => {
      if (isDragging) {
        handleDragEnd();
      }
    },
    style: {
      cursor: isDragging ? 'grabbing' : 'grab',
      touchAction: 'pan-y',
      userSelect: 'none' as 'none'
    }
  };
}; 